import React from "react";
import axios from "axios";
import { config } from "src/configs/constants";
import { Buffer } from "buffer";
// import { headers } from 'pages/login/auth'

const isApiRequest = (requestConfig) =>
  !!requestConfig?.url && requestConfig.url.startsWith(config.url.API_URL);

axios.interceptors.response.use(
  (response) => {
    if (isApiRequest(response.config)) {
      window.dispatchEvent(new CustomEvent('backend:up'));
    }
    return response;
  },
  (error) => {
    const is401 = error.response?.status === 401;
    const isAuthEndpoint = error.config?.url?.includes('/authenticate');
    // Sem token no localStorage o 401 é esperado (usuário deslogado) — não é
    // sessão perdida nem conta bloqueada, então não vale disparar aviso nenhum.
    const hasSession = !!localStorage.getItem('app-token');
    if (is401 && !isAuthEndpoint && !isImpersonating() && hasSession) {
      window.dispatchEvent(
        new CustomEvent('auth:unauthorized', {
          detail: { reason: error.response?.data?.error || '' },
        })
      );
    }
    // Heroku "Application Error" (dyno crashado/H10) não envia header CORS,
    // então o browser bloqueia a resposta e o axios reporta como Network Error sem status.
    if (isApiRequest(error.config) && !error.response && error.message === 'Network Error') {
      window.dispatchEvent(new CustomEvent('backend:down'));
    }
    return Promise.reject(error);
  }
);

export function isImpersonating() {
  return !!localStorage.getItem("__support_backup__");
}

export function token() {
  return localStorage.getItem("app-token");
}

export function headers(custom) {
  return {
    headers: {
      ...custom,
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
    },
  };
}

export const debounceEvent =
  (fn, wait = 1000, time) =>
  (...args) =>
    clearTimeout(time, (time = setTimeout(() => fn(...args), wait)));

export const apiGet = async (urlPart, params = {}) => {
  let response = {};

  const body = params.body || {};

  // console.log("body req: ", body);
  if (!("redirect" in params)) params.redirect = false;
  await axios({
    method: "get",
    url: `${config.url.API_URL}${urlPart}`,
    headers: { ...headers().headers, ...body },
    params: body,
  })
    .then((resp) => {
      response = resp.data;
      // console.log("apiGet - response: ", response);
      if (params.done) {
        params.done(resp);
      }
    })
    .catch(function (error) {
      if (params.error) {
        params.error(error.response);
        return;
      }
      const errors = {
        submitResult: error.response,
      };
      localStorage.setItem("currentUrl", window.location.pathname);

      if (error.response.status === 404) return;

      if (error.response.status === 401) {
        if (isImpersonating()) {
          const MURAL_KEYS = [
            "app_config", "mural_layouts", "mapsConfigs", "currentMapId", "mapsEditSession", "mapaEstadoFundos",
            "previsaoConfigs", "currentPrevisaoId", "previsaoEditSession", "mareConfigs",
            "conteudoConfigs", "conteudoActiveStates", "tipoLayouts", "tipoVinheta", "conteudoFormatting",
            "currentConteudoId", "currentConteudoAiId", "menuPosition", "mapType",
          ];
          const backup = JSON.parse(localStorage.getItem("__support_backup__") || "null");
          MURAL_KEYS.forEach((key) => localStorage.removeItem(key));
          localStorage.removeItem("__support_backup__");
          if (backup) {
            if (backup.muralKeys) {
              Object.entries(backup.muralKeys).forEach(([key, value]) => {
                localStorage.setItem(key, value);
              });
            }
            localStorage.setItem("app-token", backup.token);
            localStorage.setItem("accessToken", backup.token);
            localStorage.setItem("user", backup.user);
          }
          window.location.href = "/extended-sidebar/menu/home";
          return;
        }
      }
      throw new Error(errors?.submitResult?.data?.message || `API error ${errors?.submitResult?.status ?? ''}`.trim());
    });
  return response;
};

export const apiPost = async (urlPart, params) => {
  let response = {};
  const body = params.body || {};
  if (!("redirect" in params)) params.redirect = false;

  console.log("body: ", body);
  console.log("urlPart: ", urlPart);
  console.log("API_URL: ", config.url.API_URL);
  await axios
    .post(`${config.url.API_URL}${urlPart}`, body, headers())
    .then((resp) => {
      response = resp.data;
      if (params.done) {
        params.done(resp);
      }
    })
    .catch(function (error) {
      if (params.error) {
        params.error(error.response);
        return;
      }
      const errors = {
        submitResult: error.response,
      };
      localStorage.setItem("currentUrl", window.location.pathname);
      if (params.redirect) window.location.href = "/login";
      throw new Error(errors?.submitResult?.data?.message || `API error ${errors?.submitResult?.status ?? ''}`.trim());
    });
  return response;
};

export const apiPatch = async (urlPart, params) => {
  let response = {};
  const body = params.body || {};
  await axios
    .patch(`${config.url.API_URL}${urlPart}`, body, headers())
    .then((resp) => {
      response = resp.data;
      if (params.done) params.done(resp);
    })
    .catch(function (error) {
      if (params.error) { params.error(error.response); return; }
      localStorage.setItem("currentUrl", window.location.pathname);
      throw { submitResult: error.response };
    });
  return response;
};

export const apiPut = async (urlPart, params) => {
  let response = {};
  const body = params.body || {};
  await axios
    .put(`${config.url.API_URL}${urlPart}`, body, headers())
    .then((resp) => {
      response = resp.data;
      if (params.done) params.done(resp);
    })
    .catch(function (error) {
      if (params.error) { params.error(error.response); return; }
      localStorage.setItem("currentUrl", window.location.pathname);
      throw new Error(error.response?.data?.message || `API error ${error.response?.status ?? ''}`.trim());
    });
  return response;
};

/**
 * POST multipart/form-data — para upload de arquivos.
 * `params.onUploadProgress` (opcional) recebe o evento de progresso do axios.
 */
export const apiPostMultipart = async (urlPart, formData, params = {}) => {
  let response = {};
  await axios
    .post(`${config.url.API_URL}${urlPart}`, formData, {
      headers: { Authorization: `Bearer ${token()}` },
      onUploadProgress: params.onUploadProgress,
    })
    .then((resp) => { response = resp.data; })
    .catch(function (error) {
      localStorage.setItem("currentUrl", window.location.pathname);
      throw { submitResult: error.response };
    });
  return response;
};

/**
 * PATCH multipart/form-data — para upload de arquivos.
 * Não define Content-Type manualmente: o axios/browser o define automaticamente
 * com o boundary correto ao receber um FormData.
 */
export const apiPatchMultipart = async (urlPart, formData, params = {}) => {
  let response = {};
  await axios
    .patch(`${config.url.API_URL}${urlPart}`, formData, {
      headers: { Authorization: `Bearer ${token()}` },
    })
    .then((resp) => {
      response = resp.data;
      if (params.done) params.done(resp);
    })
    .catch(function (error) {
      if (params.error) { params.error(error.response); return; }
      localStorage.setItem("currentUrl", window.location.pathname);
      throw { submitResult: error.response };
    });
  return response;
};

export const apiDelete = async (urlPart, params = {}) => {
  let response = {};
  await axios
    .delete(`${config.url.API_URL}${urlPart}`, headers())
    .then((resp) => {
      response = resp.data;
      if (params.done) params.done(resp);
    })
    .catch(function (error) {
      if (params.error) { params.error(error.response); return; }
      throw { submitResult: error.response };
    });
  return response;
};

export const apiPostHeaders = async (urlPart, params) => {
  let response = {};
  const body = params.body || {};
  if (!("redirect" in params)) params.redirect = false;

  console.log("body: ", body);
  console.log("urlPart: ", urlPart);
  console.log("API_URL: ", config.url.API_URL);
  await axios
    .post(`${config.url.API_URL}${urlPart}`, body, {
      headers: {
        Authorization: headers().headers.Authorization,
        ...params.headers,
      },
    })
    .then((resp) => {
      response = resp.data;
      if (params.done) {
        params.done(resp);
      }
    })
    .catch(function (error) {
      if (params.error) {
        params.error(error.response);
        return;
      }
      const errors = {
        submitResult: error.response,
      };
      localStorage.setItem("currentUrl", window.location.pathname);
      if (params.redirect) window.location.href = "/login";
      throw new Error(errors?.submitResult?.data?.message || `API error ${errors?.submitResult?.status ?? ''}`.trim());
    });
  return response;
};

export const apiPostUrl = async (url, params) => {
  let response = {};
  const body = params.body || {};
  if (!("redirect" in params)) params.redirect = false;

  console.log("body: ", body);
  console.log("urlPart: ", url);
  console.log("API_URL: ", config.url.API_URL);
  await axios
    .post(url, body, {
      headers: {
        Authorization: headers().headers.Authorization,
        ...params.headers,
      },
    })
    .then((resp) => {
      response = resp.data;
      if (params.done) {
        params.done(resp);
      }
    })
    .catch(function (error) {
      if (params.error) {
        params.error(error.response);
        return;
      }
      const errors = {
        submitResult: error.response,
      };
      localStorage.setItem("currentUrl", window.location.pathname);
      if (params.redirect) window.location.href = "/login";
      throw new Error(errors?.submitResult?.data?.message || `API error ${errors?.submitResult?.status ?? ''}`.trim());
    });
  return response;
};

export const apiPostUrlRetry = async (url, params, maxRetries = 10, delay = 2000) => {
  const initialBody = params.body || {};
  let body = { ...initialBody };
  let allPrevisoes = [];
  let lastData = null;
  let pendingUrls = initialBody.urls || (initialBody.url ? [initialBody.url] : []);
  let attempt = 0;

  while (attempt < maxRetries) {
    attempt++;
    console.log(`Tentativa ${attempt}`);

    try {
      const resp = await axios.post(url, body, {
        headers: {
          Authorization: headers().headers.Authorization,
          ...params.headers,
        },
      });

      const data = resp.data;
      lastData = data;

      // Junta previsões válidas recebidas nesta tentativa
      if (data.previsoes?.length) {
        allPrevisoes = [...allPrevisoes, ...data.previsoes];
      }

      // Se não há erros → acabou!
      if (!data.error || data.error.length === 0) {
        const finalResponse = { ...data, previsoes: allPrevisoes };
        if (params.done) params.done({ data: finalResponse });
        return finalResponse;
      }

      // Preparar o body para reenviar SOMENTE as URLs com erro
      body = { urls: data.error };
      pendingUrls = data.error;

      console.warn(`Ainda faltam ${data.error.length} URLs. Tentando novamente...`);

      if (params.error) params.error(data.error);
    } catch (err) {
      console.warn(`Tentativa ${attempt} falhou (erro HTTP): ${err.message}`);
    }

    if (attempt < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // Quem chama decide o que fazer com o que chegou: `partial` traz as
  // previsões obtidas até aqui (null se nada chegou) e `failedUrls` as
  // URLs que continuaram falhando depois de todas as tentativas.
  const error = new Error("Limite de tentativas excedido");
  error.partial = allPrevisoes.length ? { ...lastData, previsoes: allPrevisoes, error: pendingUrls } : null;
  error.failedUrls = pendingUrls;
  throw error;
};

export const includeTrailingBackSlash = (str) => {
  str = str.replace(/\/?$/, "/");
  return str;
};

export const isEmptyObject = (object) => {
  return Object.keys(object).length === 0 && object.constructor === Object;
};

// export const superLog = (obj1, obj2, obj3, obj4, obj5) => {
//   console.log('================')
//   console.log('================')
//   console.log('================')
//   console.log(
//     JSON.stringify(obj1),
//     JSON.stringify(obj2) || '',
//     JSON.stringify(obj3) || '',
//     JSON.stringify(obj4) || '',
//     JSON.stringify(obj5) || ''
//   )
//   console.log('================')
//   console.log('================')
//   console.log('================')
// }

export const DateTime = (str) => {
  var data = new Date();

  // Guarda cada pedaço em uma variável
  var dia = data.getDate(); // 1-31
  var dia_sem = data.getDay(); // 0-6 (zero=domingo)
  var mes = data.getMonth() + 1; // 0-11 (zero=janeiro)
  var ano2 = data.getYear(); // 2 dígitos
  var ano4 = data.getFullYear(); // 4 dígitos

  var hora = data.getHours(); // 0-23
  var min = data.getMinutes(); // 0-59
  var seg = data.getSeconds(); // 0-59
  var mseg = data.getMilliseconds(); // 0-999
  var tz = data.getTimezoneOffset(); // em minutos

  // Formata a data (note o mês + 1)
  if (dia < 9) {
    dia = "0" + dia;
  }
  if (mes < 9) {
    mes = "0" + mes;
  }

  var str_data = dia + "/" + mes + "/" + ano4;

  // Formata a hora
  if (hora < 9) {
    hora = "0" + hora;
  }
  if (min < 9) {
    min = "0" + min;
  }

  var str_hora = hora + ":" + min;

  // Formata dia e hora json ex:("2021-05-12T19:03:53.548Z")
  var date_time_json =
    ano4 + "-" + mes + "-" + dia + "T" + hora + ":" + min + ":" + seg + "Z";

  // Retorno baseado no paramentro passado na requisição
  if (str === "time") {
    return str_hora;
  }

  if (str === "date") {
    return str_data;
  }

  if (str === "") {
    return data;
  }

  if (str === "json") {
    return date_time_json;
  }
};

export const weekByDate = (updatedAt, date, hour) => {
  const now = new Date(); // Data de hoje
  const past = new Date(updatedAt); // Outra data no passado

  const diff = Math.abs(now.getTime() - past.getTime()); // Subtrai uma data pela outra
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  var day = now.getDay() === past.getDay();

  var week = "";
  var weekday = past.getDay();
  switch (weekday) {
    case 0:
      week = "Domingo";
      break;
    case 1:
      week = "Segunda";
      break;
    case 2:
      week = "Terça";
      break;
    case 3:
      week = "Quarta";
      break;
    case 4:
      week = "Quinta";
      break;
    case 5:
      week = "Sexta";
      break;
    case 6:
      week = "Sábado";
      break;
    default:
  }
  // {console.log("dias: " + days + " Semana: "+ week + " Hora: " + hour + " dia: " + date)}

  if (days > 7) {
    return date;
  } else if (day) {
    return hour;
  } else {
    return week;
  }
};

export const Linkify = ({ text }) => {
  // Regex para detectar URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  // Substituir URLs por elementos <a>
  const parts = text.split(urlRegex).map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    return part;
  });
  return <p>{parts}</p>;
};

export const WhatsAppText = ({ text }) => {
  if (!text) return null;

  const parseInline = (str, keyPrefix) => {
    const regex = /(\*(\S|\S.*?\S)\*)|(_(\S|\S.*?\S)_)|(~(\S|\S.*?\S)~)|(`[^`]+`)|(https?:\/\/[^\s]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let i = 0;

    while ((match = regex.exec(str)) !== null) {
      if (match.index > lastIndex) {
        parts.push(str.slice(lastIndex, match.index));
      }
      const full = match[0];
      if (match[1]) {
        parts.push(<strong key={`${keyPrefix}-b${i}`}>{match[2]}</strong>);
      } else if (match[3]) {
        parts.push(<em key={`${keyPrefix}-i${i}`}>{match[4]}</em>);
      } else if (match[5]) {
        parts.push(<s key={`${keyPrefix}-s${i}`}>{match[6]}</s>);
      } else if (full.startsWith('`')) {
        parts.push(<code key={`${keyPrefix}-c${i}`} style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.15)', borderRadius: 3, padding: '0 3px' }}>{full.slice(1, -1)}</code>);
      } else {
        parts.push(<a key={`${keyPrefix}-u${i}`} href={full} target="_blank" rel="noopener noreferrer">{full}</a>);
      }
      lastIndex = regex.lastIndex;
      i++;
    }
    if (lastIndex < str.length) parts.push(str.slice(lastIndex));
    return parts;
  };

  const lines = text.split('\n');
  const elements = lines.map((line, li) => {
    const br = li < lines.length - 1;

    if (/^> /.test(line)) {
      return (
        <React.Fragment key={li}>
          <blockquote style={{ borderLeft: '3px solid #8696a0', margin: '1px 0', paddingLeft: '8px', color: '#8696a0' }}>
            {parseInline(line.slice(2), `${li}`)}
          </blockquote>
          {br && <br />}
        </React.Fragment>
      );
    }
    if (/^[*\-] /.test(line)) {
      return (
        <React.Fragment key={li}>
          <span style={{ display: 'flex', gap: '6px' }}>
            <span>•</span><span>{parseInline(line.slice(2), `${li}`)}</span>
          </span>
        </React.Fragment>
      );
    }
    if (/^\d+\. /.test(line)) {
      const dot = line.indexOf('. ');
      return (
        <React.Fragment key={li}>
          <span style={{ display: 'flex', gap: '6px' }}>
            <span>{line.slice(0, dot + 1)}</span><span>{parseInline(line.slice(dot + 2), `${li}`)}</span>
          </span>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={li}>
        {parseInline(line, `${li}`)}
        {br && <br />}
      </React.Fragment>
    );
  });

  return <span>{elements}</span>;
};
// export const weekByMessage = (updatedAt, date, hour) => {
//   const now = new Date() // Data de hoje
//   const past = new Date(updatedAt) // Outra data no passado

//   const diff = Math.abs(now.getTime() - past.getTime()) // Subtrai uma data pela outra
//   const days = Math.ceil(diff / (1000 * 60 * 60 * 24))

//   var day = now.getDay() === past.getDay()

//   var week = ''
//   var weekday = past.getDay()
//   switch (weekday) {
//     case 0:
//       week = 'Domingo'
//       break
//     case 1:
//       week = 'Segunda'
//       break
//     case 2:
//       week = 'Terça'
//       break
//     case 3:
//       week = 'Quarta'
//       break
//     case 4:
//       week = 'Quinta'
//       break
//     case 5:
//       week = 'Sexta'
//       break
//     case 6:
//       week = 'Sábado'
//       break
//     default:
//   }
//   // {console.log("dias: " + days + " Semana: "+ week + " Hora: " + hour + " dia: " + date)}

//   if (days > 7) {
//     return date
//   } else if (day) {
//     return 'Hoje'
//   } else {
//     return week
//   }
// }

export const decodeBase64 = (s) => {
  var e = {},
    i,
    b = 0,
    c,
    x,
    l = 0,
    a,
    r = "",
    w = String.fromCharCode,
    L = s.length;
  var A = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  for (i = 0; i < 64; i++) {
    e[A.charAt(i)] = i;
  }
  for (x = 0; x < L; x++) {
    c = e[s.charAt(x)];
    b = (b << 6) + c;
    l += 6;
    while (l >= 8) {
      ((a = (b >>> (l -= 8)) & 0xff) || x < L - 2) && (r += w(a));
    }
  }
  return r;
};

export const encodeBase64 = (e) => {
  const buf = Buffer.from(e, "utf8");
  return buf.toString("base64");
};

export const urlLastPart = (url) => {
  let locationLastPart = url;
  if (locationLastPart.substring(locationLastPart.length - 1) == "/") {
    locationLastPart = locationLastPart.substring(
      0,
      locationLastPart.length - 1
    );
  }
  locationLastPart = locationLastPart.substr(
    locationLastPart.lastIndexOf("/") + 1
  );
  return locationLastPart;
};

export const copyToClipboard = (text) => {
  if (window.clipboardData && window.clipboardData.setData) {
    // Internet Explorer-specific code path to prevent textarea being shown while dialog is visible.
    return window.clipboardData.setData("Text", text);
  } else if (
    document.queryCommandSupported &&
    document.queryCommandSupported("copy")
  ) {
    var textarea = document.createElement("textarea");
    textarea.textContent = text;
    textarea.style.position = "fixed"; // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textarea);
    textarea.select();
    try {
      return document.execCommand("copy"); // Security exception may be thrown by some browsers.
    } catch (ex) {
      // console.warn("Copy to clipboard failed.", ex);
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
};

export const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export function imageOrientation(src) {
  var orientation,
    img = new Image();
  img.src = src;

  if (img.naturalWidth > img.naturalHeight) {
    orientation = "landscape";
  } else if (img.naturalWidth < img.naturalHeight) {
    orientation = "portrait";
  } else {
    orientation = "even";
  }

  return orientation;
}

export function enableDrawingMode(canvasRef) {
  const canvas = canvasRef.current;
  const ctx = canvas.getContext("2d");

  let drawing = false;

  // Ajustar DPI para nitidez
  canvas.width = window.innerWidth * 2;
  canvas.height = window.innerHeight * 2;
  canvas.style.width = window.innerWidth + "px";
  canvas.style.height = window.innerHeight + "px";
  ctx.scale(2, 2);

  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.strokeStyle = "#c51616ff"; // cor do desenho

  const startDraw = (e) => {
    drawing = true;
    const { x, y } = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!drawing) return;
    const { x, y } = getPos(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDraw = () => {
    drawing = false;
    ctx.beginPath();
  };

  // Mouse
  canvas.addEventListener("mousedown", startDraw);
  canvas.addEventListener("mousemove", draw);
  canvas.addEventListener("mouseup", stopDraw);

  // Touch (mobile)
  canvas.addEventListener("touchstart", startDraw);
  canvas.addEventListener("touchmove", draw);
  canvas.addEventListener("touchend", stopDraw);

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect();

    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  return () => {
    // Cleanup quando desativar o modo desenho
    canvas.removeEventListener("mousedown", startDraw);
    canvas.removeEventListener("mousemove", draw);
    canvas.removeEventListener("mouseup", stopDraw);
    canvas.removeEventListener("touchstart", startDraw);
    canvas.removeEventListener("touchmove", draw);
    canvas.removeEventListener("touchend", stopDraw);
  };
}
